import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { ArrowLeft, ArrowRight, Loader2, FileText, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

import StepClientDetails from './wizard/StepClientDetails';
import StepWebsiteDeveloper from './wizard/StepWebsiteDeveloper';
import StepBrandAssets from './wizard/StepBrandAssets';
import StepDocuments from './wizard/StepDocuments';
import StepBotConfig from './wizard/StepBotConfig';
import StepReview from './wizard/StepReview';
import { 
  AdminTenantFormData, 
  initialFormData, 
  ADMIN_WIZARD_DRAFT_KEY_PREFIX,
  AdminWizardDraft,
  SerializableAdminFormData
} from './wizard/types';
import { useAdminAuth } from '@/hooks/useAdminAuth';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const STEPS = [
  { id: 1, title: 'Client Details', description: 'Basic business information' },
  { id: 2, title: 'Website & Developer', description: 'URLs and contact info' },
  { id: 3, title: 'Brand Assets', description: 'Logo, colors, and files' },
  { id: 4, title: 'Documents', description: 'FAQs and training docs' },
  { id: 5, title: 'Bot Config', description: 'Specialisations and rules' },
  { id: 6, title: 'Review', description: 'Confirm and create' },
];

const AdminAddTenantWizard: React.FC<Props> = ({ open, onOpenChange, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [data, setData] = useState<AdminTenantFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);
  const [savedDraft, setSavedDraft] = useState<AdminWizardDraft | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const { user } = useAdminAuth();

  // Storage helpers
  const getStorageKey = () => `${ADMIN_WIZARD_DRAFT_KEY_PREFIX}-${user?.id}`;

  const saveDraft = () => {
    if (!user?.id) return;
    const { logoFile, brandGuidelinesFile, fontFiles, documentFiles, ...serializable } = data;
    const draft: AdminWizardDraft = {
      data: serializable as SerializableAdminFormData,
      step,
      savedAt: Date.now(),
      businessName: data.businessName || 'Untitled',
    };
    try {
      localStorage.setItem(getStorageKey(), JSON.stringify(draft));
      setLastSaved(new Date());
    } catch (e) {
      console.error('Failed to save draft:', e);
    }
  };

  const loadDraft = (): AdminWizardDraft | null => {
    if (!user?.id) return null;
    try {
      const stored = localStorage.getItem(getStorageKey());
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  };

  const clearDraft = () => {
    if (!user?.id) return;
    try {
      localStorage.removeItem(getStorageKey());
      setLastSaved(null);
    } catch (e) {
      console.error('Failed to clear draft:', e);
    }
  };

  // Check for existing draft when dialog opens
  useEffect(() => {
    if (open && user?.id) {
      const draft = loadDraft();
      if (draft && draft.data.businessName) {
        setSavedDraft(draft);
        setShowRestorePrompt(true);
      }
    }
  }, [open, user?.id]);

  // Auto-save on data/step changes (debounced)
  useEffect(() => {
    if (!open || !user?.id) return;
    if (!data.businessName) return; // Only save if there's meaningful data
    
    const timer = setTimeout(() => {
      saveDraft();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [data, step, open, user?.id]);

  const handleRestoreDraft = () => {
    if (savedDraft) {
      setData({ 
        ...initialFormData, 
        ...savedDraft.data,
        // Reset file fields
        logoFile: null,
        brandGuidelinesFile: null,
        fontFiles: [],
        documentFiles: [],
      });
      setStep(savedDraft.step);
      toast.info('Draft restored. Please re-upload any files.');
    }
    setShowRestorePrompt(false);
    setSavedDraft(null);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setShowRestorePrompt(false);
    setSavedDraft(null);
  };

  const updateData = (updates: Partial<AdminTenantFormData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  };

  const resetForm = () => {
    setStep(1);
    setData(initialFormData);
    clearDraft();
  };

  const handleClose = () => {
    // Don't clear draft on close - preserve for later
    setStep(1);
    setData(initialFormData);
    onOpenChange(false);
  };

  const validateStep = (currentStep: number): boolean => {
    switch (currentStep) {
      case 1:
        if (!data.businessName || !data.contactName || !data.email || !data.useCase) {
          toast.error('Please fill in all required fields');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
          toast.error('Please enter a valid email address');
          return false;
        }
        return true;
      case 2:
        if (!data.websiteUrl || !data.developerEmail || !data.summaryForwardEmail) {
          toast.error('Please fill in all required fields');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const goNext = () => {
    if (validateStep(step)) {
      setStep((s) => Math.min(s + 1, 6));
    }
  };

  const goBack = () => {
    setStep((s) => Math.max(s - 1, 1));
  };

  const uploadFile = async (
    tenantId: string,
    file: File,
    assetType: string
  ): Promise<string | null> => {
    const path = `${tenantId}/${assetType}/${file.name}`;
    const { error } = await supabase.storage
      .from('tenant-assets')
      .upload(path, file, { upsert: true });

    if (error) {
      console.error(`Error uploading ${assetType}:`, error);
      return null;
    }
    return path;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // Get current user session
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        toast.error('You must be logged in to perform this action');
        setIsSubmitting(false);
        return;
      }

      // First, call the edge function to create tenant and get tenant ID
      const response = await supabase.functions.invoke('admin-provision-tenant', {
        body: {
          packageType: data.packageType,
          businessName: data.businessName,
          contactName: data.contactName,
          email: data.email,
          phone: data.phone || null,
          industry: data.industry,
          useCase: data.useCase,
          websiteUrl: data.websiteUrl,
          additionalUrls: data.additionalUrls.filter(Boolean),
          developerEmail: data.developerEmail,
          installScope: data.installScope,
          summaryForwardEmail: data.summaryForwardEmail,
          brandColors: data.brandColors,
          useWebsiteAsDesignReference: data.useWebsiteAsDesignReference,
          specialisations: data.specialisations,
          specialisationsOther: data.specialisationsOther,
          doNotMention: data.doNotMention,
          internalNotes: data.internalNotes,
          sendClientNotification: data.sendClientNotification,
        },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to provision tenant');
      }

      const { tenantId } = response.data;

      if (!tenantId) {
        throw new Error('No tenant ID returned');
      }

      // Upload files to storage
      const uploadPromises: Promise<void>[] = [];

      if (data.logoFile) {
        uploadPromises.push(
          uploadFile(tenantId, data.logoFile, 'logo').then(() => {})
        );
      }

      if (data.brandGuidelinesFile) {
        uploadPromises.push(
          uploadFile(tenantId, data.brandGuidelinesFile, 'brand-guidelines').then(() => {})
        );
      }

      data.fontFiles.forEach((file) => {
        uploadPromises.push(
          uploadFile(tenantId, file, 'fonts').then(() => {})
        );
      });

      data.documentFiles.forEach((file) => {
        uploadPromises.push(
          uploadFile(tenantId, file, 'documents').then(() => {})
        );
      });

      await Promise.all(uploadPromises);

      // Clear draft on successful submission
      clearDraft();
      
      toast.success('Tenant provisioned successfully!');
      handleClose();
      onSuccess?.();
    } catch (error: any) {
      console.error('Provisioning error:', error);
      toast.error(error.message || 'Failed to provision tenant');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return <StepClientDetails data={data} updateData={updateData} />;
      case 2:
        return <StepWebsiteDeveloper data={data} updateData={updateData} />;
      case 3:
        return <StepBrandAssets data={data} updateData={updateData} />;
      case 4:
        return <StepDocuments data={data} updateData={updateData} />;
      case 5:
        return <StepBotConfig data={data} updateData={updateData} />;
      case 6:
        return <StepReview data={data} updateData={updateData} />;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Restore Draft Prompt */}
      <AlertDialog open={showRestorePrompt} onOpenChange={setShowRestorePrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Draft Found
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>You have an unsaved tenant draft:</p>
              <p className="font-medium text-foreground">
                "{savedDraft?.businessName}" - Step {savedDraft?.step}
              </p>
              <p className="text-xs text-muted-foreground">
                Last edited: {savedDraft ? formatDistanceToNow(savedDraft.savedAt, { addSuffix: true }) : ''}
              </p>
              <p className="text-amber-600 text-sm mt-2">
                Note: Previously uploaded files will need to be re-uploaded.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleDiscardDraft}>Start Fresh</AlertDialogCancel>
            <AlertDialogAction onClick={handleRestoreDraft}>Continue Draft</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-center justify-between">
            <DialogTitle>Add New Tenant</DialogTitle>
            {lastSaved && data.businessName && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Clock className="h-3 w-3" />
                Draft saved
              </span>
            )}
          </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center justify-between mb-6 px-1">
          {STEPS.map((s, index) => (
            <React.Fragment key={s.id}>
              <div
                className={`flex flex-col items-center ${
                  step >= s.id ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium border-2 transition-colors ${
                    step > s.id
                      ? 'bg-primary border-primary text-primary-foreground'
                      : step === s.id
                      ? 'border-primary text-primary'
                      : 'border-muted-foreground/30'
                  }`}
                >
                  {s.id}
                </div>
                <span className="text-xs mt-1 hidden md:block">{s.title}</span>
              </div>
              {index < STEPS.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 ${
                    step > s.id ? 'bg-primary' : 'bg-muted'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Step Title */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold">{STEPS[step - 1].title}</h3>
          <p className="text-sm text-muted-foreground">
            {STEPS[step - 1].description}
          </p>
        </div>

        {/* Step Content */}
        <div className="min-h-[300px]">{renderStepContent()}</div>

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={step === 1 ? handleClose : goBack}
            disabled={isSubmitting}
          >
            {step === 1 ? (
              'Cancel'
            ) : (
              <>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back
              </>
            )}
          </Button>

          {step < 6 ? (
            <Button onClick={goNext}>
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Tenant'
              )}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default AdminAddTenantWizard;
